import React from 'react';

export interface ProgressStep {
  id: string;
  label: string;
  description?: string;
}

export interface ProgressIndicatorProps {
  steps: ProgressStep[];
  currentStep: string;
  className?: string;
  showLabels?: boolean;
  variant?: 'default' | 'compact';
}

const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({
  steps,
  currentStep,
  className = '',
  showLabels = true,
  variant = 'default',
}) => {
  const currentIndex = steps.findIndex(step => step.id === currentStep);
  const currentStepData = steps[currentIndex];
  const nextStepData = steps[currentIndex + 1];

  // If current step is confirmation, all steps should be completed
  const isConfirmationStep = currentStep === 'confirmation';

  return (
    <div className={`mb-4 ${className}`}>
      {/* Desktop view - full progress indicator */}
      <div className="hidden md:flex justify-center">
        <div className="flex items-center">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className="flex flex-col items-center">
                <div
                  className={`${
                    variant === 'compact' ? 'w-6 h-6' : 'w-8 h-8'
                  } rounded-full flex items-center justify-center text-xs font-medium transition-colors duration-200 ${
                    currentStep === step.id
                      ? 'bg-blue-500 text-white'
                      : currentIndex > index || isConfirmationStep
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {currentIndex > index || isConfirmationStep ? (
                    <svg
                      className="w-3 h-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    index + 1
                  )}
                </div>
                {showLabels && (
                  <span
                    className={`mt-1 text-xs font-medium text-center transition-colors duration-200 ${
                      currentStep === step.id
                        ? 'text-blue-600'
                        : currentIndex > index || isConfirmationStep
                          ? 'text-green-600'
                          : 'text-gray-400'
                    }`}
                  >
                    {step.label}
                  </span>
                )}
              </div>
              {index < steps.length - 1 && (
                <div className="mx-4 -mt-4">
                  <div
                    className={`w-8 h-0.5 rounded-full transition-colors duration-200 ${
                      currentIndex > index || isConfirmationStep
                        ? 'bg-green-400'
                        : 'bg-gray-200'
                    }`}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Mobile view - current step, next step, and progress indicator */}
      <div className="md:hidden">
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          {/* Progress indicator */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
                {isConfirmationStep ? steps.length : currentIndex + 1}
              </div>
              <div className="text-sm font-medium text-gray-700">
                Step {isConfirmationStep ? steps.length : currentIndex + 1} of{' '}
                {steps.length}
              </div>
            </div>
            <div className="text-xs text-gray-500">
              {isConfirmationStep
                ? '100%'
                : `${Math.round(((currentIndex + 1) / steps.length) * 100)}% Complete`}
            </div>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
            <div
              className={`${isConfirmationStep ? 'bg-green-500' : 'bg-blue-500'} h-2 rounded-full transition-all duration-300`}
              style={{
                width: isConfirmationStep
                  ? '100%'
                  : `${((currentIndex + 1) / steps.length) * 100}%`,
              }}
            />
          </div>

          {!isConfirmationStep && (
            <div className="flex flex-row gap-3">
              {/* Current step */}
              <div className="flex-1">
                <div className="text-xs font-medium text-blue-600 uppercase tracking-wide">
                  Current Step
                </div>
                <div className="text-sm font-medium text-gray-900 mt-1">
                  {currentStepData?.label}
                </div>
                {currentStepData?.description && (
                  <div className="text-xs text-gray-600 mt-1">
                    {currentStepData.description}
                  </div>
                )}
              </div>

              {/* Next step (if exists) */}
              {nextStepData && (
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Next Step
                  </div>
                  <div className="text-sm text-gray-700 mt-1">
                    {nextStepData.label}
                  </div>
                  {nextStepData.description && (
                    <div className="text-xs text-gray-500 mt-1">
                      {nextStepData.description}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProgressIndicator;
